'use client'

import { useEffect, useMemo, useRef } from 'react'

import { env } from '@/env'
import { useAnalyzify } from 'analyzify-js/react'

import { toStorefrontProductFromHit } from './Analyzify.adapters'
import { isAnalyzifyEnabled, VIEW_STAGE_PRICE } from './Analyzify.constants'

import type { BaseHit, Hit } from 'instantsearch.js'

type AnalyzifyCollectionTrackerProps = {
  hits: Hit<BaseHit>[]
  /** Numeric Shopify id - reported as item_list_id. */
  collectionId?: string
  /** Full Shopify GID, for the SDK's page context. */
  collectionGid?: string
  collectionHandle?: string
  /** Title shoppers see - reported as item_list_name. */
  collectionTitle?: string
}

/**
 * Fires view_item_list for an Algolia-backed collection listing.
 */
const Tracker = ({ hits, collectionId, collectionGid, collectionHandle, collectionTitle }: AnalyzifyCollectionTrackerProps) => {
  const { track, isReady, setPageContext } = useAnalyzify()
  const trackedQuery = useRef<string | null>(null)

  const products = useMemo(() => hits.map((hit) => toStorefrontProductFromHit(hit, VIEW_STAGE_PRICE)), [hits])

  useEffect(() => {
    if (!isReady || products.length === 0) return
    const fingerprint = `${collectionId ?? ''}|${products.map((product) => product.id).join(',')}`
    if (trackedQuery.current === fingerprint) return

    trackedQuery.current = fingerprint

    setPageContext({
      type: 'collection',
      collection: {
        id: collectionGid ?? '',
        title: collectionTitle ?? '',
        handle: collectionHandle ?? '',
        products: { edges: products.map((product) => ({ node: product })) },
      },
    })

    track.viewItemList(products, collectionTitle ?? '', collectionId)

    // Do not reset the ref in cleanup - that would double-fire under Strict Mode.
  }, [isReady, products, collectionId, collectionGid, collectionHandle, collectionTitle, setPageContext, track])

  return null
}

/** Fires view_item_list for a collection listing. Renders nothing. */
export const AnalyzifyCollectionTracker = (props: AnalyzifyCollectionTrackerProps) => {
  if (!isAnalyzifyEnabled(env.NEXT_PUBLIC_ANALYZIFY_MODE)) return null

  return <Tracker {...props} />
}
